import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Heart, Leaf, Users, Star, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import SEOMeta from '../components/SEOMeta';

const fadeUp = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const values = [
  { icon: Heart, title: 'Rispetto e Dignità', desc: 'Ogni animale merita un addio degno della vita che ha donato. Trattiamo ogni caso con cura e rispetto assoluti.' },
  { icon: Leaf, title: 'Cura per la Natura', desc: 'Offriamo soluzioni eco-sostenibili perché il ritorno alla terra è parte del ciclo della vita.' },
  { icon: Users, title: 'Comunità e Supporto', desc: 'Non sei solo. La nostra community e il nostro team di supporto sono sempre presenti per accompagnarti.' },
  { icon: Star, title: 'Eccellenza nel Servizio', desc: 'Anni di esperienza, personale formato e dedicato, tecnologie moderne per un servizio impeccabile.' },
  { icon: Shield, title: 'Trasparenza', desc: 'Prezzi chiari, processi trasparenti, nessuna sorpresa. La tua fiducia è il nostro bene più prezioso.' },
  { icon: Clock, title: 'Sempre Disponibili', desc: 'Disponibili 24 ore su 24, 7 giorni su 7. Perché il dolore non ha orari, e nemmeno noi.' }
];


export default function ChiSiamoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Chi Siamo"
        description="Scopri il team di Una Zampa in Paradiso: professionisti con anni di esperienza nella cremazione animali, supporto nel lutto e servizi memoriali. La tua fiducia è il nostro bene più prezioso."
        url="/chi-siamo"
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1665541773616-2b5fde2a5af0?w=1400&q=85')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-deep)]/80 via-[var(--color-deep)]/55 to-transparent" />
        <div className="relative container-custom py-24 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-xl">
            <div className="inline-flex items-center gap-2 text-[var(--color-terracotta-soft)] text-xs font-bold uppercase tracking-widest mb-4">
              <Heart size={12} fill="currentColor" /> La nostra storia
            </div>
            <h1 className="font-serif text-5xl font-semibold text-white mb-4">{t('about.title')}</h1>
            <p className="text-white/80 text-lg">{t('about.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-py">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--color-sage)] mb-3">{t('about.mission_title')}</div>
              <h2 className="font-serif text-4xl font-semibold text-[var(--color-deep)] mb-6">Nati dall'amore per gli animali</h2>
              <p className="text-[var(--color-muted-text)] leading-relaxed mb-4">
                Una Zampa in Paradiso nasce da un profondo rispetto per il legame unico e insostituibile tra le persone e i loro animali. Crediamo che ogni vita — grande o piccola, lunga o breve — meriti un addio degno, circondato d'amore. Per questo offriamo un servizio di cremazione individuale, dove ogni animale viene trattato con la stessa cura e dignità di un membro della famiglia.
              </p>
              <p className="text-[var(--color-muted-text)] leading-relaxed mb-8">
                Ogni giorno siamo al fianco di famiglie nel momento più delicato della perdita. Non ci limitiamo a un servizio: accompagniamo, ascoltiamo, sosteniamo. Perché sappiamo che chi ha amato un animale sa quanto quel legame sia reale, prezioso e — per sempre — indelebile nel cuore.
              </p>
              <div className="flex gap-8">
                <div className="text-center">
                  <div className="font-serif text-3xl font-semibold text-[var(--color-sage)]">2000+</div>
                  <div className="text-xs text-[var(--color-muted-text)] mt-1">Famiglie accompagnate</div>
                </div>
                <div className="text-center">
                  <div className="font-serif text-3xl font-semibold text-[var(--color-sage)]">4+</div>
                  <div className="text-xs text-[var(--color-muted-text)] mt-1">Anni di esperienza</div>
                </div>
                <div className="text-center">
                  <div className="font-serif text-3xl font-semibold text-[var(--color-sage)]">H24</div>
                  <div className="text-xs text-[var(--color-muted-text)] mt-1">Sempre disponibili</div>
                </div>
              </div>
            </motion.div>

            {/* Logo grande al posto della foto */}
            <motion.div {...fadeUp} className="flex items-center justify-center">
              <div className="bg-[var(--color-sage-soft)] rounded-3xl p-10 shadow-xl flex flex-col items-center gap-4 w-full max-w-sm">
                <img
                  src="/logo.png"
                  alt="Una Zampa in Paradiso"
                  className="w-full h-auto object-contain rounded-2xl"
                />
                <div className="flex items-center gap-2 text-[var(--color-sage)] text-xs font-semibold uppercase tracking-widest">
                  <Heart size={12} fill="currentColor" className="animate-pulse" />
                  <span>Amici per Sempre</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-py bg-[var(--color-paper)]">
        <div className="container-custom">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="font-serif text-4xl font-semibold text-[var(--color-deep)]">{t('about.values_title')}</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-[var(--color-cream)] border border-[var(--color-border)] rounded-2xl p-6"
              >
                <div className="w-11 h-11 bg-[var(--color-sage-soft)] rounded-xl flex items-center justify-center mb-4">
                  <v.icon size={20} className="text-[var(--color-sage)]" />
                </div>
                <h4 className="font-serif font-semibold text-[var(--color-deep)] mb-2">{v.title}</h4>
                <p className="text-sm text-[var(--color-muted-text)] leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Frase motivante del Team */}
      <section className="section-py">
        <div className="container-custom">
          <motion.div
            {...fadeUp}
            className="max-w-3xl mx-auto text-center"
          >
            {/* Virgolette decorative */}
            <div className="font-serif text-8xl leading-none text-[var(--color-sage)]/25 select-none mb-2">&ldquo;</div>

            <blockquote className="font-serif text-2xl sm:text-3xl text-[var(--color-deep)] leading-snug mb-8 italic">
              Non esiste un amore troppo grande da piangere.<br />
              Il tuo dolore è la misura di quanto hai saputo amare —<br />
              e questo rende il tuo amico eternamente speciale.
            </blockquote>

            <div className="font-serif text-8xl leading-none text-[var(--color-sage)]/25 select-none rotate-180 mb-8">&ldquo;</div>

            <div className="flex items-center justify-center gap-3">
              <div className="h-px w-16 bg-[var(--color-border)]" />
              <div className="flex items-center gap-2 text-[var(--color-terracotta)] text-sm font-semibold uppercase tracking-widest">
                <Heart size={13} fill="currentColor" className="animate-pulse" />
                Il Team di Una Zampa in Paradiso
              </div>
              <div className="h-px w-16 bg-[var(--color-border)]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[var(--color-deep)]">
        <div className="container-custom text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-4xl font-semibold text-white mb-4">Possiamo aiutarti?</h2>
            <p className="text-[var(--color-stone)] max-w-md mx-auto mb-8">Il nostro team è sempre disponibile per rispondere alle tue domande e guidarti in questo momento.</p>
            <Button size="lg" className="bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl px-8 py-6" onClick={() => navigate('/contatti')}>Contattaci</Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
